import type { CognitoryCitationVerificationResult } from "../api/cognitoryCitationVerificationClient";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
} from "../theme/mainPageVisual";

type CognitoryCitationSourcePanelProps = {
  open: boolean;
  loading: boolean;
  result: CognitoryCitationVerificationResult | null;
  openingSource: boolean;
  onClose: () => void;
  onOpenSource?: () => void;
};

const PANEL_ERROR_MESSAGES: Record<string, string> = {
  unauthenticated: "Sign in to inspect this citation.",
  forbidden: "You do not have access to this citation source.",
  not_found: "This citation source could not be verified.",
  temporary_failure: "Citation verification is temporarily unavailable.",
  invalid: "This citation could not be opened.",
};

export default function CognitoryCitationSourcePanel({
  open,
  loading,
  result,
  openingSource,
  onClose,
  onOpenSource,
}: CognitoryCitationSourcePanelProps) {
  if (!open) {
    return null;
  }

  const verified = !loading && result?.kind === "verified" ? result : null;
  const canOpenSource =
    verified?.sourceOpenEnabled === true && typeof onOpenSource === "function";

  return (
    <div
      data-testid="cognitory-citation-source-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Citation source"
      style={{
        marginTop: "1rem",
        padding: "1rem 1.1rem",
        borderRadius: 12,
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: MAIN_PAGE_COLORS.panelFill,
        fontFamily: MAIN_PAGE_FONT_STACK,
      }}
    >
      <div style={{ marginBottom: "0.75rem" }}>
        <p
          style={{
            margin: 0,
            fontSize: "0.78rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: MAIN_PAGE_COLORS.whisper,
          }}
        >
          Citation source
        </p>
        {verified ? (
          <h3
            data-testid="cognitory-citation-source-heading"
            style={{
              margin: "0.35rem 0 0",
              fontSize: "1rem",
              color: MAIN_PAGE_COLORS.slate,
              fontWeight: 500,
              lineHeight: 1.35,
            }}
          >
            [{verified.citationNumber}] {verified.documentTitle}
          </h3>
        ) : null}
      </div>

      {loading ? (
        <p
          data-testid="cognitory-citation-source-loading"
          style={{ margin: 0, color: MAIN_PAGE_COLORS.muted }}
        >
          Verifying citation source…
        </p>
      ) : null}

      {verified ? (
        <>
          <p
            data-testid="cognitory-citation-source-passage"
            style={{
              margin: "0 0 0.85rem",
              lineHeight: 1.55,
              color: MAIN_PAGE_COLORS.slate,
              fontSize: "0.95rem",
            }}
          >
            <span aria-hidden="true">&ldquo;</span>
            <mark
              data-testid="cognitory-citation-source-highlight"
              style={{
                background: "rgba(59, 130, 246, 0.18)",
                color: MAIN_PAGE_COLORS.slate,
                padding: "0.1rem 0.15rem",
                borderRadius: 4,
              }}
            >
              {verified.citedPassage}
            </mark>
            <span aria-hidden="true">&rdquo;</span>
          </p>
          <div
            data-testid="cognitory-citation-source-actions"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                data-testid="cognitory-citation-source-open"
                disabled={!canOpenSource || openingSource}
                onClick={() => onOpenSource?.()}
                style={{
                  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                  background: canOpenSource
                    ? "rgba(255, 255, 255, 0.75)"
                    : "rgba(248, 250, 252, 0.8)",
                  borderRadius: 8,
                  padding: "0.35rem 0.75rem",
                  cursor:
                    canOpenSource && !openingSource ? "pointer" : "not-allowed",
                  color: canOpenSource
                    ? MAIN_PAGE_COLORS.link
                    : MAIN_PAGE_COLORS.muted,
                  fontFamily: MAIN_PAGE_FONT_STACK,
                  fontSize: "0.88rem",
                  opacity: canOpenSource ? 1 : 0.7,
                }}
              >
                {openingSource ? "Opening source…" : "Open source"}
              </button>
            </div>
            <button
              type="button"
              data-testid="cognitory-citation-source-close"
              onClick={onClose}
              style={{
                border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                background: "transparent",
                borderRadius: 8,
                padding: "0.35rem 0.75rem",
                cursor: "pointer",
                color: MAIN_PAGE_COLORS.muted,
                fontFamily: MAIN_PAGE_FONT_STACK,
                fontSize: "0.88rem",
              }}
            >
              Close
            </button>
          </div>
        </>
      ) : null}

      {!loading && result && result.kind !== "verified" ? (
        <>
          <p
            data-testid="cognitory-citation-source-error"
            style={{ margin: "0 0 0.85rem", color: MAIN_PAGE_COLORS.muted }}
          >
            {PANEL_ERROR_MESSAGES[result.kind]}
          </p>
          <div
            data-testid="cognitory-citation-source-actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              data-testid="cognitory-citation-source-close"
              onClick={onClose}
              style={{
                border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                background: "transparent",
                borderRadius: 8,
                padding: "0.35rem 0.75rem",
                cursor: "pointer",
                color: MAIN_PAGE_COLORS.muted,
                fontFamily: MAIN_PAGE_FONT_STACK,
                fontSize: "0.88rem",
              }}
            >
              Close
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
